"use client"

import type React from "react"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { useTranslation } from "react-i18next"
import { FcGoogle } from "react-icons/fc"
import { toast } from "sonner"
import { CheckCircle, ChevronLeft, Eye, EyeOff, Loader2, User } from "lucide-react"

import { azureDb } from "@/lib/azure/compat-client"
import { OTP_CODE_LENGTH } from "@/lib/azure/compat-client"
import { notifyAzureAuthChanged } from "@/lib/azure/auth-events"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { getOAuthCallbackUrl } from "@/lib/app-url"
import { CountryOption, getCountryDisplayName, getFallbackCountries } from "@/lib/countries"
import { LegalNotice } from "@/components/legal/legal-notice"

export function ApplicantSignupPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const otpLength = OTP_CODE_LENGTH
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState("")
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otpCode, setOtpCode] = useState("")
  const [pendingUserId, setPendingUserId] = useState<string | null>(null)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [isResendingOtp, setIsResendingOtp] = useState(false)
  const [countryOpen, setCountryOpen] = useState(false)
  const [countries, setCountries] = useState<CountryOption[]>(() => getFallbackCountries())
  const [countriesLoading, setCountriesLoading] = useState(false)

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    gender: "",
    nationality: "",
    location: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false,
    agreeMarketing: false,
  })

  const { t, i18n } = useTranslation("applicant-signup")

  useEffect(() => {
    const authError = searchParams.get("auth_error")
    if (authError) setError(authError)
  }, [searchParams])

  useEffect(() => {
    let isMounted = true

    const fetchCountries = async () => {
      setCountriesLoading(true)

      try {
        const response = await fetch("https://restcountries.com/v3.1/all?fields=name,translations,cca2")

        if (!response.ok) {
          throw new Error("Failed to fetch countries")
        }

        const data = (await response.json()) as CountryOption[]
        const sortedCountries = data.sort((a, b) => a.name.common.localeCompare(b.name.common))

        if (isMounted) {
          setCountries(sortedCountries)
        }
      } catch (error) {
        console.error("Failed to load countries:", error)
        if (isMounted) {
          setCountries(getFallbackCountries())
        }
      } finally {
        if (isMounted) {
          setCountriesLoading(false)
        }
      }
    }

    fetchCountries()

    return () => {
      isMounted = false
    }
  }, [])

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      setError(t("fullNameRequired"))
      return false
    }
    if (!formData.email.trim()) {
      setError(t("emailRequired"))
      return false
    }
    if (!formData.phone.trim()) {
      setError(t("phoneRequired"))
      return false
    }
    if (!formData.gender) {
      setError(t("genderRequired"))
      return false
    }
    if (!formData.nationality.trim()) {
      setError(t("nationalityRequired"))
      return false
    }
    if (!formData.password) {
      setError(t("passwordRequired"))
      return false
    }
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long.")
      return false
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t("passwordMismatch"))
      return false
    }
    if (!formData.agreeTerms) {
      setError(t("agreeTermsRequired"))
      return false
    }
    return true
  }

  const createApplicantProfile = async (userId: string) => {
    const normalizedEmail = formData.email.trim().toLowerCase()

    const { data: existingProfile, error: existingProfileError } = await azureDb
      .from("job_seekers")
      .select("id")
      .eq("id", userId)
      .maybeSingle()

    if (existingProfileError) {
      console.error("Existing applicant lookup error:", existingProfileError)
    }

    if (existingProfile) {
      const { error: updateError } = await azureDb
        .from("job_seekers")
        .update({
          full_name: formData.fullName.trim(),
          phone_number: formData.phone.trim(),
          gender: formData.gender,
          nationality: formData.nationality.trim(),
          location: formData.location.trim() || null,
          profile_completed: true,
        })
        .eq("id", userId)
      if (updateError) throw updateError

      const completion = await fetch(
        "/api/auth/external/complete-onboarding",
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            termsAccepted: formData.agreeTerms,
            marketingEmails: formData.agreeMarketing,
          }),
        },
      )
      const result = await completion.json().catch(() => ({}))
      if (!completion.ok) {
        throw new Error(result.error || "Account setup could not be completed.")
      }
      await notifyAzureAuthChanged()
      return
    }

    const { error: createProfileError } = await azureDb.from("job_seekers").insert({
      id: userId,
      email: normalizedEmail,
      full_name: formData.fullName.trim(),
      phone_number: formData.phone.trim(),
      gender: formData.gender,
      nationality: formData.nationality.trim(),
      location: formData.location.trim() || null,
      profile_completed: true,
      technical_skills: [],
      soft_skills: [],
      languages: [],
      certifications: [],
      previous_companies: [],
      industries: [],
      strengths: [],
      weaknesses: [],
      recommendations: [],
    })

    if (createProfileError) {
      throw createProfileError
    }

    // Signup already collected every field the onboarding gate requires, so
    // complete it now instead of sending the applicant to a second data page.
    const completion = await fetch("/api/auth/external/complete-onboarding", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        termsAccepted: formData.agreeTerms,
        marketingEmails: formData.agreeMarketing,
      }),
    })
    const result = await completion.json().catch(() => ({}))
    if (!completion.ok) {
      throw new Error(result.error || "Account setup could not be completed.")
    }
    await notifyAzureAuthChanged()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      const checkResponse = await fetch("/api/check-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          type: "job_seeker",
        }),
      })

      if (checkResponse.ok) {
        const data = await checkResponse.json()
        if (data.exists) {
          if (data.conflictType === "company") {
            setError(t("employerEmailConflict"))
          } else {
            setError(t("emailAlreadyExists"))
          }
          setIsLoading(false)
          return
        }
      }

      const { data: authData, error: authError } = await azureDb.auth.signUp({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        options: {
          data: {
            user_type: "job_seeker",
            full_name: formData.fullName.trim(),
            phone_number: formData.phone.trim(),
            gender: formData.gender,
            nationality: formData.nationality.trim(),
            location: formData.location.trim(),
          },
        },
      })

      if (authError) {
        setError(authError.message)
        return
      }

      if (!authData.user) {
        setError(t("accountCreationFailed"))
        return
      }

      setPendingUserId(authData.user.id)
      setShowOtpModal(true)
      toast.success(t("accountCreatedCheckEmail"))
    } catch (signupError) {
      console.error("Applicant signup error:", signupError)
      setError(t("unexpectedError"))
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignup = async () => {
    setError("")
    document.cookie = "google_auth_intent=job_seeker; path=/; max-age=600; SameSite=Lax"

    const { error: oauthError } = await azureDb.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: getOAuthCallbackUrl(window.location.origin),
      },
    })

    if (oauthError) {
      setError(oauthError.message)
    }
  }

  const handleOtpVerification = async () => {
    if (!pendingUserId || otpCode.length !== otpLength) {
      setError(t("invalidOtpCode"))
      return
    }

    setIsVerifyingOtp(true)
    setError("")

    try {
      const { data: verifyData, error: verifyError } = await azureDb.auth.verifyOtp({
        email: formData.email.trim().toLowerCase(),
        token: otpCode,
        type: "email",
      })

      if (verifyError) {
        setError(`${t("verificationFailed")}: ${verifyError.message}`)
        return
      }

      if (!verifyData.user) {
        setError(t("verificationFailedNoUser"))
        return
      }

      await createApplicantProfile(verifyData.user.id)

      toast.success(t("emailVerifiedSuccess"))
      router.push("/applicant/dashboard")
    } catch (verificationError) {
      console.error("Applicant OTP verification error:", verificationError)
      setError(t("profileCreationFailed"))
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  const handleResendOtp = async () => {
    setIsResendingOtp(true)
    setError("")

    try {
      const { error: resendError } = await azureDb.auth.resend({
        type: "signup",
        email: formData.email.trim().toLowerCase(),
      })

      if (resendError) {
        if (resendError.message.includes("rate limit")) {
          setError(t("tooManyRequests"))
        } else {
          setError(resendError.message)
        }
        return
      }

      toast.success(t("otpResent"))
    } catch (resendError) {
      console.error("Resend OTP error:", resendError)
      setError(t("otpResendFailed"))
    } finally {
      setIsResendingOtp(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6 rtl:space-x-reverse">
          <Link href="/" className="hover:text-blue-600">
            {t("home")}
          </Link>
          <span>{">"}</span>
          <span className="text-gray-900">{t("signUp")}</span>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="flex items-center space-x-3 mb-8 rtl:space-x-reverse">
            <Link href="/">
              <Button variant="ghost" size="icon">
                <ChevronLeft className={`w-5 h-5 ${i18n.language === "ar" ? "rotate-180" : ""}`} />
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">{t("createAccount")}</h1>
          </div>

          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle>{t("createAccount")}</CardTitle>
              <CardDescription>{t("createAccountDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert className="mb-6 border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full-name">
                      {t("fullName")} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="full-name"
                      required
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      placeholder={t("fullNamePlaceholder")}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      {t("email")} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={t("emailPlaceholder")}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">
                    {t("location", { defaultValue: "Location" })}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal">
                        <span className="truncate">
                          {formData.location
                            ? getCountryDisplayName(
                              countries.find((country) => country.name.common === formData.location) || { name: { common: formData.location } },
                              i18n.language,
                            )
                            : countriesLoading ? t("loadingCountries") : t("locationPlaceholder", { defaultValue: "Select a country" })}
                        </span>
                        <ChevronLeft className={`h-4 w-4 shrink-0 opacity-50 ${i18n.language === "ar" ? "-rotate-90" : "rotate-90"}`} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput placeholder={t("searchCountries")} />
                        <CommandList>
                          <CommandEmpty>{t("noCountryFound")}</CommandEmpty>
                          <CommandGroup>
                            {countries.map((country) => (
                              <CommandItem
                                key={`location-${country.name.common}`}
                                value={country.name.common}
                                keywords={[country.name.common, getCountryDisplayName(country, i18n.language)]}
                                onSelect={() => setFormData({ ...formData, location: country.name.common })}
                              >
                                {getCountryDisplayName(country, i18n.language)}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">
                      {t("phone")} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder={t("phonePlaceholder")}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gender">
                      {t("gender")} <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                      <SelectTrigger id="gender">
                        <SelectValue placeholder={t("selectGender")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">{t("male")}</SelectItem>
                        <SelectItem value="female">{t("female")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nationality">
                    {t("nationality")} <span className="text-red-500">*</span>
                  </Label>
                  <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="nationality"
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={countryOpen}
                        className="w-full justify-between font-normal"
                        disabled={countriesLoading && countries.length === 0}
                      >
                        <span className="truncate">
                          {formData.nationality
                            ? getCountryDisplayName(
                              countries.find((country) => country.name.common === formData.nationality) || { name: { common: formData.nationality } },
                              i18n.language
                            )
                            : countriesLoading
                              ? t("loadingCountries")
                              : t("selectNationality")}
                        </span>
                        <ChevronLeft className={`h-4 w-4 shrink-0 opacity-50 ${i18n.language === "ar" ? "-rotate-90" : "rotate-90"}`} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput placeholder={t("searchCountries")} />
                        <CommandList>
                          <CommandEmpty>{t("noCountryFound")}</CommandEmpty>
                          <CommandGroup>
                            {countries.map((country) => (
                              <CommandItem
                                key={country.name.common}
                                value={country.name.common}
                                keywords={[
                                  country.name.common,
                                  getCountryDisplayName(country, i18n.language),
                                ]}
                                onSelect={() => {
                                  setFormData({ ...formData, nationality: country.name.common })
                                  setCountryOpen(false)
                                }}
                              >
                                <CheckCircle
                                  className={`mr-2 h-4 w-4 ${formData.nationality === country.name.common ? "opacity-100" : "opacity-0"}`}
                                />
                                {getCountryDisplayName(country, i18n.language)}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">
                      {t("password")} <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        required
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder={t("passwordPlaceholder")}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">{t("passwordHint")}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">
                      {t("confirmPassword")} <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        placeholder={t("confirmPasswordPlaceholder")}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Checkbox
                      id="terms"
                      checked={formData.agreeTerms}
                      onCheckedChange={(checked) => setFormData({ ...formData, agreeTerms: checked as boolean })}
                      required
                    />
                    <Label htmlFor="terms" className="text-sm">
                      {t("agreeTerms")}{" "}
                      <Link href="/legal/terms-of-service" target="_blank" className="text-blue-600 hover:underline">
                        (Read Terms of Service)
                      </Link>{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Checkbox
                      id="marketing"
                      checked={formData.agreeMarketing}
                      onCheckedChange={(checked) => setFormData({ ...formData, agreeMarketing: checked as boolean })}
                    />
                    <Label htmlFor="marketing" className="text-sm">
                      {t("agreeMarketing")}{" "}
                      <Link href="/legal/privacy-policy" target="_blank" className="text-blue-600 hover:underline">
                        (Read Privacy Policy)
                      </Link>
                    </Label>
                  </div>

                  <LegalNotice kind="pilot" />
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("creatingAccount")}
                    </>
                  ) : (
                    t("createAccount")
                  )}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">{t("orContinueWith")}</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                size="lg"
                onClick={handleGoogleSignup}
                disabled={isLoading}
              >
                <FcGoogle className="w-5 h-5 mr-2" />
                {t("signUpWithGoogle")}
              </Button>

              <Separator className="my-6" />

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  {t("alreadyHaveAccount")}{" "}
                  <Link href="/auth/applicant/login" className="text-blue-600 hover:text-blue-800 font-medium">
                    {t("signIn")}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t("verifyYourEmail")}</h2>
              <p className="text-gray-600 mb-6">
                {t("otpSentDescription")} <strong>{formData.email}</strong>. {t("otpVerifyInstructions")}
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {isVerifyingOtp && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-blue-800 font-medium">{t("verifyingOtp")}</span>
                  </div>
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleOtpVerification() }} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp">{t("otpCodeLabel")} *</Label>
                  <Input
                    id="otp"
                    type="text"
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, otpLength))}
                    placeholder={t("otpCodePlaceholder")}
                    disabled={isVerifyingOtp}
                    maxLength={otpLength}
                    className="text-center text-lg tracking-widest"
                  />
                  <p className="text-xs text-gray-500">{t("otpCodeHint")}</p>
                </div>

                <div className="space-y-3 pt-4">
                  <Button type="submit" className="w-full" disabled={isVerifyingOtp || otpCode.length !== otpLength}>
                    {isVerifyingOtp ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("verifying")}
                      </>
                    ) : (
                      t("verifyAndComplete")
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowOtpModal(false)
                      setOtpCode("")
                      setError("")
                      setPendingUserId(null)
                    }}
                    className="w-full"
                    disabled={isVerifyingOtp}
                  >
                    {t("cancel")}
                  </Button>
                </div>
              </form>

              <div className="text-center mt-4">
                <Button
                  type="button"
                  variant="link"
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={handleResendOtp}
                  disabled={isResendingOtp || isVerifyingOtp}
                >
                  {isResendingOtp ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      {t("resending")}
                    </>
                  ) : (
                    t("resendCode")
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
